import api from "./api";

/**
 * Issue #618 — THE MERGE HALF OF THE DUPLICATE REPORT'S VERDICT.
 *
 * Martin, 2026-09-04: "The possible duplicate form also does not have a
 * button to say it is a duplicate and what action needs to be taken." He was
 * right, and the gap was wider than the page: `POST /api/grounds/:id/merge`
 * has been admin-gated and working since #131, and nothing in the frontend
 * had ever called it. Detection shipped; resolution was curl-only.
 *
 * --- WHY THE RULES ARE HERE AND NOT IN THE JSX ---
 *
 * `lib/adminVenues.js`'s rule, for the same reason: this repo mounts no
 * components in unit tests, so a rule written inside a component is a rule
 * nothing can assert. Every decision that can send this verb at the wrong
 * record lives in this file, where a test can hold it.
 *
 * --- THE ONE THAT DESTROYS THE WRONG ROW ---
 *
 * `POST /api/grounds/:id/merge { into_ground_id }` addresses THE LOSER. The
 * ground in the path is the ground that gets retired; the ground in the body
 * survives. Swap them and the merge runs, succeeds, and retires the record
 * the reviewer chose to keep — with no undo in the app.
 *
 * So `mergeGroundPair` takes a NAMED OBJECT, never two positional ids. A
 * caller cannot transpose `{ loserId, survivorId }` by accident the way it
 * can transpose `merge(a, b)`, and the one place that maps a survivor choice
 * onto that pair — `mergeDirection` — is a pure function with its own tests.
 *
 * --- NO DEFAULT SURVIVOR, EVER ---
 *
 * `mergeDirection` answers `null` for a missing choice, an unrecognised id,
 * and a malformed pair. It never picks. #618's rule is that the survivor is
 * chosen explicitly and that no path merges into a default, and the way to
 * make that true is for the function that decides direction to have no
 * fallback branch to reach — not for the component to remember to check.
 * The scan's top false positive is two genuinely distinct grounds at one
 * venue (`Oval No.1` / `Oval No.2`, a main ground and its practice field
 * 80 m away), scored near-certain; a pre-selected survivor plus one press is
 * how those get fused.
 */

/**
 * The sentence the confirm step must say out loud. Exported so the component
 * cannot paraphrase it into something softer, and so a test can hold the
 * claim rather than a string that happens to be in the file.
 *
 * "From here" is exact, and the precision is the point: nothing is deleted
 * by a merge — the retired row survives and still resolves (#131) — but
 * there is no unmerge in the product, and an admin who reads "cannot be
 * undone" as "the data is gone" is being told something worse than the
 * truth, while one who reads it as "someone can flip it back" is being told
 * something better than the truth.
 */
export const MERGE_UNDO_WARNING =
  "This cannot be undone from here. Bookmarks, matches and training sessions move to the ground you keep, and the other record leaves the directory for good.";

/** `GET /api/admin/grounds/duplicates/impact/:a/:b` — what a merge of this
 * pair would move, counted on both records because the survivor has not been
 * chosen yet. Admin-only, read-only, and 404 for any pair the scan does not
 * currently propose. */
export async function fetchPairMergeImpact(groundAId, groundBId) {
  const { data } = await api.get(
    `/admin/grounds/duplicates/impact/${encodeURIComponent(groundAId)}/${encodeURIComponent(groundBId)}`,
  );
  return data;
}

/**
 * `POST /api/grounds/:loserId/merge` — #131's route, unchanged. The FIRST
 * frontend caller it has ever had.
 *
 * Named arguments, and a refusal rather than a request when they are not two
 * distinct non-empty ids. A merge issued with a missing survivor would be a
 * 400 at worst and a merge into the wrong record at best; refusing here
 * means the mistake cannot leave the browser.
 */
export async function mergeGroundPair({ loserId, survivorId }) {
  if (!loserId || !survivorId || loserId === survivorId) {
    throw new Error("a merge needs two different grounds and an explicit survivor");
  }
  const { data } = await api.post(`/grounds/${encodeURIComponent(loserId)}/merge`, {
    into_ground_id: survivorId,
  });
  return data;
}

/** Both sides of a scanned pair as a plain list, in the order the server
 * returned them, so the two choices are always rendered the same way round
 * for the same pair. */
export function survivorChoices(pair) {
  return [pair?.a, pair?.b].filter((g) => g && g.ground_id);
}

/**
 * Which record survives and which is retired, given the reviewer's pick.
 *
 * `null` unless `survivorId` is EXACTLY one of the pair's two ids. No
 * default, no "first one wins", no coercion — see this module's header.
 */
export function mergeDirection(pair, survivorId) {
  const choices = survivorChoices(pair);
  if (choices.length !== 2) return null;
  if (choices[0].ground_id === choices[1].ground_id) return null;
  if (!survivorId) return null;
  const survivor = choices.find((g) => g.ground_id === survivorId);
  if (!survivor) return null;
  const loser = choices.find((g) => g.ground_id !== survivorId);
  return { survivor, loser };
}

/** Whether the merge button may fire at all. One expression, so the button's
 * `disabled` and the request's own guard cannot drift apart. */
export function canMerge(pair, survivorId) {
  return mergeDirection(pair, survivorId) !== null;
}

/** Zeroes, not `undefined`, for a side the preview has no row for — a
 * missing count must read as "nothing moves", never as a blank where a
 * number belongs. */
const NO_IMPACT = { bookmarks: 0, matches: 0, training_sessions: 0, facilities: 0 };

/**
 * One side's counts out of the pair preview, matched BY GROUND ID rather
 * than by which key it sits under. The server sends `ground_id` with each
 * side precisely so this cannot silently attach the survivor's history to
 * the loser's confirm line.
 */
export function impactFor(impact, groundId) {
  if (!impact || !groundId) return { ...NO_IMPACT };
  for (const side of [impact.a, impact.b]) {
    if (side && side.ground_id === groundId) {
      return {
        bookmarks: Number(side.bookmarks) || 0,
        matches: Number(side.matches) || 0,
        training_sessions: Number(side.training_sessions) || 0,
        facilities: Number(side.facilities) || 0,
      };
    }
  }
  return { ...NO_IMPACT };
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * What moves, in the numbers the reviewer needs before pressing — one line
 * per kind of record, and every line present even when it is zero.
 *
 * ZEROES ARE RENDERED, NOT HIDDEN. "0 matches" is the fact that makes a
 * merge safe to press; an absent line reads as "not checked", and the
 * difference between those two is the whole value of showing counts at all.
 *
 * THE BOOKMARK LINE IS SPLIT WHEN IT HAS TO BE. A player who bookmarked
 * BOTH grounds cannot have their loser-side bookmark repointed — the unique
 * index on (player, ground) makes that a constraint error — so `mergeGround`
 * deletes it and they keep the one they already had on the survivor. Saying
 * "12 saved grounds move" when two of them are removed instead would be a
 * number that does not match the merge's own report a second later.
 */
export function describeMergeImpact(impact, loserId) {
  const counts = impactFor(impact, loserId);
  const shared = Math.min(Number(impact?.shared_bookmark_players) || 0, counts.bookmarks);
  const moved = counts.bookmarks - shared;
  return [
    shared > 0
      ? `${plural(counts.bookmarks, "saved ground", "saved grounds")}: ${moved} ${moved === 1 ? "moves" : "move"} to the ground you keep and ${shared} ${shared === 1 ? "is" : "are"} removed because ${shared === 1 ? "that player has" : "those players have"} already saved both`
      : `${plural(moved, "saved ground moves", "saved grounds move")} to the ground you keep`,
    `${plural(counts.matches, "match", "matches")} ${counts.matches === 1 ? "is" : "are"} rewritten to the ground you keep`,
    `${plural(counts.training_sessions, "training session", "training sessions")} ${counts.training_sessions === 1 ? "is" : "are"} rewritten to the ground you keep`,
    `${plural(counts.facilities, "recorded surface moves", "recorded surfaces move")} across`,
  ];
}

/**
 * "Pellmoor Field · Whangarei, New Zealand · verified · added 3 Aug 2026" — the
 * one line under each choice, so the pick is made on the record rather than
 * on which button is on the left.
 *
 * The pin is NOT in here: it is rendered as coordinates by the pair card
 * itself, at the precision the report already uses, and a second copy at a
 * different precision would be two claims about one number.
 */
export function describeSurvivorChoice(ground) {
  if (!ground) return "";
  const where = [ground.city, ground.country].filter(Boolean).join(", ");
  const when = ground.created_at ? new Date(ground.created_at) : null;
  const added =
    when && !Number.isNaN(when.getTime())
      ? `added ${when.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}`
      : null;
  return [where || null, ground.is_verified ? "verified" : null, added].filter(Boolean).join(" · ");
}

/**
 * The line the reviewer reads at the confirm step, naming both records the
 * way round the merge will actually run.
 *
 * Named rather than positional in the sentence too ("Keep X, retire Y"), for
 * the same reason `mergeGroundPair` takes an object: "Merge X into Y" is
 * read both ways round by real people under time pressure, and this is the
 * last thing shown before the press.
 */
export function describeMergeDirection(direction) {
  if (!direction) return "";
  return `Keep “${direction.survivor.name}”, retire “${direction.loser.name}”.`;
}

/**
 * What the merge REPORTED, once it has run — the same four kinds of record
 * the preview promised, read back off `POST /grounds/:id/merge`'s response.
 *
 * Shown because a destructive verb that says only "done" leaves the admin
 * with no way to check that the history followed. The numbers here are the
 * merge's own counts (`MergeCounts`), not the preview's, so a disagreement
 * between the two sentences is visible rather than assumed away.
 */
export function describeMergeResult(result) {
  if (!result) return "Merged.";
  const n = (v) => Number(v) || 0;
  const parts = [
    `${plural(n(result.moved_bookmarks), "saved ground moved", "saved grounds moved")}`,
    n(result.dropped_duplicate_bookmarks) > 0
      ? `${n(result.dropped_duplicate_bookmarks)} removed as already saved on the survivor`
      : null,
    `${plural(n(result.moved_matches), "match", "matches")} and ${plural(n(result.moved_training_sessions), "training session", "training sessions")} rewritten`,
    `${plural(n(result.moved_facilities), "recorded surface", "recorded surfaces")} moved`,
  ].filter(Boolean);
  return `Merged. ${parts.join(", ")}.`;
}
